import classNames from 'classnames';
import reactUpdate from 'immutability-helper';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import {assert} from 'Asserts';
import ModelTypeConstants, {EntryType, SchemaType} from 'constants/ModelTypeConstants';
import {Batch} from 'constants/SampleConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {BioentitySummary} from 'folder-items/FileConstants';
import {
  DataTableConfig,
  FromServerDataTableColumn,
  FromServerDataTableConfig,
} from 'notebook/data-tables/DataTableConstants';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {RowData} from 'notebook/tables/constants/RowConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import DataTableConfigFieldSelector from 'notebook/tables/lookup-field/DataTableConfigFieldSelector';
import FiltersSelector from 'notebook/tables/lookup-field/FiltersSelector';
import {
  DATA_TYPE,
  FieldsChainField,
  generateExpCondLookupSpecifier,
  generateInputSampleLookupSpecifier,
  generateLookupSpecifier,
  isFieldBatchSchemaLink,
} from 'notebook/tables/lookup-field/LookupConfigurationUtil';
import {ExternalCache} from 'notebook/tables/lookup-field/LookupFieldCacheConstants';
import {
  compileLookupFieldFunction,
  LOOKUP_FIELD_FIELD_TYPE_INPUT_SAMPLE_BATCH,
  LOOKUP_FIELD_FIELD_TYPE_INPUT_SAMPLE_BATCH_ENTITY,
  LOOKUP_FIELD_FIELD_TYPE_INPUT_SAMPLE_ENTITY,
  LOOKUP_FIELD_FIELD_TYPE_SAMPLE_BATCH,
  LOOKUP_FIELD_FIELD_TYPE_SAMPLE_BATCH_ENTITY,
  LOOKUP_FIELD_FIELD_TYPE_SAMPLE_ENTITY,
} from 'notebook/tables/lookup-field/LookupFieldDefinitionParser';
import LookupFieldSchemaFieldSelector from 'notebook/tables/lookup-field/LookupFieldSchemaFieldSelector';
import {
  getBatchId,
  getFileId,
  getSampleSpecifierFromTable,
  getStageRunId,
  mergeServerLookupResultIn,
} from 'notebook/tables/lookup-field/LookupFieldUtil';
import LookupFieldValuesTable from 'notebook/tables/lookup-field/LookupFieldValuesTable';
import NestedSchemaFieldSelector from 'notebook/tables/lookup-field/NestedSchemaFieldSelector';
import {BenchlingState} from 'redux-stores/reduceApp';
import StorableStore from 'samples/StorableStore';
import {FIELD_TYPES, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {
  TagSchemaSummaryWithFieldsGraphQL,
  useTagSchemaSummariesWithFields,
} from 'schemas/useTagSchemaSummariesWithFields';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import {ExperimentalConditionValue} from 'workflows/WorkflowConstants';

import LookupFieldDataTypeSelector from './LookupFieldDataTypeSelector';
import {
  FilterSpecifier,
  InputSampleLookupSpecifier,
  LOOKUP_TYPES,
  LookupSpecifier,
  LookupType,
  ServerLookupResult,
  StageRunSpecifierFieldType,
} from './LookupFieldDefinitionConstants';
import LookupFieldSchemaSelector from './LookupFieldSchemaSelector';
import LookupTypeSelector from './LookupTypeSelector';

interface ReduxConnectedProps {
  batchId: string | null;
  fileId: string | null | undefined;
  file: BioentitySummary | null;
  experimentalConditionNames: Array<string>;
}

const mapStateToProps = (
  state: BenchlingState,
  props: BaseConfigureLookupFieldModalProps
): ReduxConnectedProps => {
  const workflowSelector = new WorkflowSelector(state.models.workflows);
  const {rowData, stageConfigurationId, evaluatedRow, columnDefinitions} = props;

  let experimentalConditionNames = [];
  const stageRunId = getStageRunId({evaluatedRow, rowData, columnDefinitions});
  if (stageRunId != null) {
    const stageRun = workflowSelector.getStageRun(stageRunId);
    assert(stageRun != null, 'Could not find stage run in workflow store', {rowData});
    const experimentalConditionValues = stageRun.stageFlowNode.experimentalConditionValues;
    // eslint-disable-next-line no-restricted-syntax
    experimentalConditionNames = _.uniq(
      experimentalConditionValues.map((value: ExperimentalConditionValue) => value.name)
    );
  } else if (stageConfigurationId != null) {
    const stageConfiguration = workflowSelector.getStageConfiguration(stageConfigurationId);
    assert(stageConfiguration != null, 'Could not find stage entry template in workflow store', {
      stageConfigurationId,
    });
    // eslint-disable-next-line no-restricted-syntax
    experimentalConditionNames = _.pluck(stageConfiguration.experimentalConditions, 'name');
  }

  const fileId = rowData == null ? null : getFileId(workflowSelector, rowData);
  return {
    batchId: rowData == null ? null : getBatchId(workflowSelector, rowData),
    fileId,
    file: fileId == null ? null : state.models.fileSummariesById[fileId],
    experimentalConditionNames,
  };
};

interface StorableStoreConnectedProps {
  batch: Batch | null;
}

const mapStorableStoreToProps = (
  storableStore: typeof StorableStore,
  props: BaseConfigureLookupFieldModalProps & ReduxConnectedProps
): StorableStoreConnectedProps => ({
  batch: props.batchId == null ? null : storableStore.getBatch(props.batchId),
});

const SCHEMA_TYPE_TO_DISPLAY: Record<SchemaType, string> = {
  [ModelTypeConstants.TAG_SCHEMA]: 'Entity ID',
  [ModelTypeConstants.BATCH_SCHEMA]: 'Batch ID',
};

// State that is relevant when lookupType is SAMPLE or STAGE_RUN_INPUT
interface SampleLookupState {
  // The tag schema of the top level sample and the type (Batch/Entity)
  topLevelSchema: TagSchemaSummaryWithFieldsGraphQL | null;
  topLevelSchemaType: SchemaType;

  // Nested fields. When nested fields are selected the LOOKUPFIELD will have nested SOURCE argument.
  // The last field corresponds to the topLevelSchema.
  fieldsChain: Array<FieldsChainField>;

  // The type of the data to retrieve
  dataType: DATA_TYPE;

  // Schema type for the SAMPLE_ID data type
  sampleSchemaTypeToInsert: SchemaType;
  // Field on selectedLink{Tag,Batch}Schema linking to the item selected in the schema field dropdown.
  selectedSchemaField: SchemaField | null;
  selectedSchemaFieldSchemaType: SchemaType | null;
  // Data table lookup arguments
  dataTableConfig: FromServerDataTableConfig | null;
  dataTableColumn: FromServerDataTableColumn | null;
  filterSpecifiers: Array<FilterSpecifier>;
}

// State that is relevant when lookupType is EXPERIMENTAL_CONDITION
interface ExperimentalConditionState {
  experimentalConditionName: string | null;
}

// LOOKUPFIELD query results
interface ResultsState {
  serverLookupResult: ServerLookupResult | null;
}

interface State extends SampleLookupState, ExperimentalConditionState, ResultsState {
  lookupType: LookupType;
}

interface BaseConfigureLookupFieldModalProps {
  onRequestClose: () => void;
  onRequestSubmit: ({formula, newExternalCache}: {formula: string; newExternalCache?: ExternalCache}) => void;
  entryType: EntryType;
  // ID of the stage entry template, or null if the modal is opened outside of a stage entry template.
  stageConfigurationId: string | null;
  dataTableConfig: DataTableConfig | null;
  supportedLookupTypes: Array<LookupType>;

  rowData: RowData | null;
  evaluatedRow: Array<EvaluatedCell> | null;
  columnDefinitions: Array<ColumnDefinition> | null;

  tagSchemasById: Record<string, TagSchemaSummaryWithFieldsGraphQL>;
}

type ConnectedProps = ReduxConnectedProps & StorableStoreConnectedProps;

export type ConfigureLookupFieldModalProps = BaseConfigureLookupFieldModalProps & ConnectedProps;

export class ConfigureLookupFieldModal extends React.Component<ConfigureLookupFieldModalProps, State> {
  override state: State = this._getClearState();

  override componentDidMount(): void {
    if (this.props.fileId != null && !this.props.file) {
      FileBrowserActions.bulkFetchFileSummaries([this.props.fileId]);
    }
  }

  override UNSAFE_componentWillReceiveProps(nextProps: ConfigureLookupFieldModalProps): void {
    if (this.props.fileId == null && nextProps.fileId != null && !nextProps.file) {
      FileBrowserActions.bulkFetchFileSummaries([nextProps.fileId]);
    }
    if (this.state.topLevelSchema == null) {
      // To handle the case when batch/file are loaded after the state is initialized
      this.setState({topLevelSchema: this._getTableRowSampleTagSchema(nextProps)});
    }
  }

  _getInitialLookupType(): LookupType {
    const initialLookupType = LOOKUP_TYPES.find((lookupType) =>
      this.props.supportedLookupTypes.includes(lookupType)
    );
    assert(initialLookupType != null, 'No supported lookup types found', {
      supportedLookupTypes: this.props.supportedLookupTypes,
    });
    return initialLookupType;
  }

  _getClearState(): State {
    const lookupType = this._getInitialLookupType();
    const topLevelSchema = this._getTableRowSampleTagSchema(this.props);

    return {
      lookupType,

      topLevelSchema,
      topLevelSchemaType:
        lookupType === LookupType.SAMPLE
          ? this._getTopLevelSchemaTypeForSampleLookupType()
          : ModelTypeConstants.TAG_SCHEMA,

      fieldsChain: [],

      dataType: DATA_TYPE.DATA_TABLE,

      sampleSchemaTypeToInsert: ModelTypeConstants.TAG_SCHEMA,

      selectedSchemaField: null,
      selectedSchemaFieldSchemaType: null,

      dataTableConfig: null,
      dataTableColumn: null,
      filterSpecifiers: [],

      experimentalConditionName: null,

      ...this._getClearResultsState(),
    };
  }

  _getClearResultsState(): ResultsState {
    return {
      serverLookupResult: null,
    };
  }

  _handleChangeLookupType = (lookupType: LookupType): void => {
    let {topLevelSchema, topLevelSchemaType} = this.state;
    if (lookupType === LookupType.SAMPLE) {
      topLevelSchema = this._getTableRowSampleTagSchema(this.props);
      topLevelSchemaType = this._getTopLevelSchemaTypeForSampleLookupType();
    }

    this.setState({
      ...this._getClearState(),
      lookupType,
      topLevelSchema,
      topLevelSchemaType,
    });
  };

  _getSelectedLinkSchemasFromFieldsChain(fieldsChain: Array<FieldsChainField>): {
    tagSchema: TagSchemaSummaryWithFieldsGraphQL | null;
    batchSchema: null;
  } | null {
    if (this.state.fieldsChain.length === 0) {
      return null;
    }
    // Figure out what schema the deepest link (fieldsChain[0]) points to so that _renderSchemaFieldSelectors
    // knows what fields to show and _getLookupSpecifier knows what schema ID to put in the lookup definition.
    const selectedLinkField = fieldsChain[0];
    assert(!isFieldBatchSchemaLink(selectedLinkField), 'Selected link field is a batch');

    const tagSchema = this.props.tagSchemasById[selectedLinkField.requiredLink.tagSchema!.id];

    return {tagSchema, batchSchema: null};
  }

  _handleTopLevelTagSchemaChanged = (
    tagSchema: TagSchemaSummaryWithFieldsGraphQL | null,
    schemaType: SchemaType
  ): void => {
    this.setState({
      ...this._getClearState(),
      lookupType: this.state.lookupType,
      topLevelSchema: tagSchema,
      topLevelSchemaType: schemaType,
    });
  };

  _handleSelectedFieldsChainChange = (fieldsChain: Array<FieldsChainField>): void => {
    const {lookupType, topLevelSchema, topLevelSchemaType} = this.state;
    this.setState({
      ...this._getClearState(),
      lookupType,
      topLevelSchema,
      topLevelSchemaType,
      fieldsChain,
    });
  };

  _handleFieldValuesChange = (serverLookupResult: ServerLookupResult): void => {
    this.setState({
      serverLookupResult,
    });
  };

  _getFilteredFilterSpecifiers(): Array<FilterSpecifier> {
    return this.state.filterSpecifiers.filter(
      (specifier) =>
        specifier.fieldFilter.value != null &&
        specifier.fieldFilter.value.length > 0 &&
        specifier.fieldFilter.name != null &&
        specifier.fieldFilter.name.length > 0
    );
  }

  _getInputSampleLookupSpecifier(): InputSampleLookupSpecifier | null {
    if (this.state.lookupType !== LookupType.STAGE_RUN_INPUT) {
      return null;
    }

    let fieldType: StageRunSpecifierFieldType;
    if (this.state.sampleSchemaTypeToInsert != null) {
      switch (this.state.topLevelSchemaType) {
        case ModelTypeConstants.BATCH_SCHEMA:
          fieldType =
            this.state.sampleSchemaTypeToInsert === ModelTypeConstants.BATCH_SCHEMA
              ? LOOKUP_FIELD_FIELD_TYPE_INPUT_SAMPLE_BATCH
              : LOOKUP_FIELD_FIELD_TYPE_INPUT_SAMPLE_BATCH_ENTITY;
          break;
        case ModelTypeConstants.TAG_SCHEMA:
          fieldType = LOOKUP_FIELD_FIELD_TYPE_INPUT_SAMPLE_ENTITY;
          break;
        default:
          throw new Error(`Unexpected lookup type ${this.state.sampleSchemaTypeToInsert}`);
      }
    } else {
      switch (this.state.topLevelSchemaType) {
        case ModelTypeConstants.BATCH_SCHEMA:
          fieldType = LOOKUP_FIELD_FIELD_TYPE_INPUT_SAMPLE_BATCH;
          break;
        case ModelTypeConstants.TAG_SCHEMA:
          fieldType = LOOKUP_FIELD_FIELD_TYPE_INPUT_SAMPLE_ENTITY;
          break;
        default:
          throw new Error(`Unexpected schema type ${this.state.topLevelSchemaType}`);
      }
    }

    const {evaluatedRow, rowData, columnDefinitions} = this.props;
    return generateInputSampleLookupSpecifier({
      fieldType,
      schemaId: this.state.topLevelSchema != null ? this.state.topLevelSchema.id : null,
      stageRunId: getStageRunId({evaluatedRow, rowData, columnDefinitions}),
      stageRunSampleId: (this.props.rowData && this.props.rowData.stageRunSampleId) || null,
    });
  }

  _hasRowData(): boolean {
    const {batchId, fileId, evaluatedRow, rowData, columnDefinitions} = this.props;
    return (
      rowData != null &&
      Boolean(
        batchId != null || fileId != null || getStageRunId({evaluatedRow, rowData, columnDefinitions}) != null
      )
    );
  }

  /**
   * Get the LOOKUPFIELD field type for looking up a sample batch or entity.
   */
  _getSampleSchemaFieldType(): StageRunSpecifierFieldType | null {
    if (this.state.dataType !== DATA_TYPE.SAMPLE_ID) {
      return null;
    }

    const {sampleSchemaTypeToInsert} = this.state;
    switch (sampleSchemaTypeToInsert) {
      case ModelTypeConstants.BATCH_SCHEMA:
        return LOOKUP_FIELD_FIELD_TYPE_SAMPLE_BATCH;
      case ModelTypeConstants.TAG_SCHEMA:
        return this._canLookupBatchId()
          ? LOOKUP_FIELD_FIELD_TYPE_SAMPLE_BATCH_ENTITY
          : LOOKUP_FIELD_FIELD_TYPE_SAMPLE_ENTITY;
      default:
        throw new Error(`Unexpected schema type ${sampleSchemaTypeToInsert}`);
    }
  }

  _getLookupSpecifierForSchemaLookup(sourceLookupSpecifier: LookupSpecifier | null): LookupSpecifier | null {
    const {
      topLevelSchema,
      fieldsChain,
      dataType,
      selectedSchemaField,
      selectedSchemaFieldSchemaType,
      dataTableConfig,
      dataTableColumn,
    } = this.state;
    if (
      topLevelSchema != null &&
      dataType === DATA_TYPE.DATA_TABLE &&
      (dataTableConfig == null || dataTableColumn == null)
    ) {
      return null;
    }
    const {tagSchema, batchSchema} = this._getSelectedLinkSchemas();
    return generateLookupSpecifier({
      topLevelSchema,
      fieldsChain,
      dataType,
      selectedSchemaField,
      selectedSchemaFieldSchemaType,
      selectedLinkTagSchema: tagSchema,
      selectedLinkBatchSchema: batchSchema,
      sampleSchemaFieldType: this._getSampleSchemaFieldType(),
      dataTableConfig,
      dataTableColumn,
      filterSpecifiers: this._getFilteredFilterSpecifiers(),
      sourceLookupSpecifier,
    });
  }

  _getLookupSpecifier(): LookupSpecifier | null {
    if (this.state.lookupType === LookupType.EXPERIMENTAL_CONDITION) {
      if (this.state.experimentalConditionName == null) {
        return null;
      }
      const {evaluatedRow, rowData, columnDefinitions} = this.props;
      return generateExpCondLookupSpecifier({
        experimentalConditionName: this.state.experimentalConditionName,
        stageRunId: getStageRunId({evaluatedRow, rowData, columnDefinitions}),
        stageRunSampleId: (rowData && rowData.stageRunSampleId) || null,
      });
    } else {
      const inputSampleLookupSpecifier = this._getInputSampleLookupSpecifier();
      if (inputSampleLookupSpecifier != null && this.state.topLevelSchema == null) {
        // No further lookups are possible
        return inputSampleLookupSpecifier;
      }

      if (this.state.dataType === DATA_TYPE.INPUT_SAMPLE) {
        assert(
          this.state.lookupType === LookupType.STAGE_RUN_INPUT && this.state.fieldsChain.length === 0,
          'Input sample lookup is not allowed'
        );
        assert(inputSampleLookupSpecifier != null, 'inputSampleLookupSpecifier expected to exist');
        return inputSampleLookupSpecifier;
      }

      return this._getLookupSpecifierForSchemaLookup(this._getInputSampleLookupSpecifier());
    }
  }

  _handleSubmit = (): void => {
    const lookupSpecifier = this._getLookupSpecifier();
    assert(lookupSpecifier != null, 'Submit should not be enabled if data is missing');
    let modalResult: {formula: string; newExternalCache?: ExternalCache} = {
      formula: `=${compileLookupFieldFunction(lookupSpecifier)}`,
    };

    if (this._hasRowData()) {
      const {serverLookupResult} = this.state;
      const {evaluatedRow, rowData, columnDefinitions} = this.props;

      const rowSampleSpecifier = getSampleSpecifierFromTable({
        evaluatedRow,
        rowData: rowData!, // _hasRowData() check above asserts rowData exists
        columnDefinitions,
      });
      assert(
        rowSampleSpecifier != null && serverLookupResult != null,
        'Submit is expected to be disabled when rowData is available and either ' +
          'rowSampleSpecifier or serverLookupResult are null'
      );
      const newExternalCache = mergeServerLookupResultIn({
        serverLookupResult,
        externalCache: {LOOKUPFIELD: []},
        rowSampleSpecifier,
      });

      modalResult = {
        ...modalResult,
        newExternalCache,
      };
    }
    this.props.onRequestSubmit(modalResult);
  };

  _isSetButtonDisabled(): boolean {
    return (
      this._getLookupSpecifier() == null || (this._hasRowData() && this.state.serverLookupResult == null)
    );
  }

  _getButtons(): Array<ModalButton> {
    return [
      {
        key: 'set',
        onClick: this._handleSubmit,
        style: ButtonStyle.PRIMARY,
        submit: true,
        disabled: this._isSetButtonDisabled(),
        text: 'Set',
      },
    ];
  }

  _handleAddFilter = (): void => {
    this.setState({
      filterSpecifiers: [
        ...this.state.filterSpecifiers,
        {
          fieldFilter: {
            name: null,
            value: null,
          },
        },
      ],
    });
  };

  _handleDeleteFilter = (filterIndex: number): void => {
    this.setState(
      reactUpdate(this.state, {
        filterSpecifiers: {
          $splice: [[filterIndex, 1]],
        },
      })
    );
  };

  _handleEditFilter = (filterIndex: number, tagSchemaField: SchemaField, value: string): void => {
    this.setState(
      reactUpdate(this.state, {
        filterSpecifiers: {
          [filterIndex]: {
            $set: {
              fieldFilter: {name: tagSchemaField == null ? '' : tagSchemaField.name, value},
            },
          },
        },
      })
    );
  };

  _canTableRowSampleBeBatch(): boolean {
    const isTemplate = [ModelTypeConstants.STAGE_CONFIGURATION, ModelTypeConstants.TEMPLATE].includes(
      this.props.entryType
    );

    // Entity samples in non-template entries
    if (!isTemplate && this.props.batchId == null) {
      return false;
    }
    // Entity-link data tables in templates
    if (
      isTemplate &&
      this.props.dataTableConfig &&
      this.props.dataTableConfig.linkType === FIELD_TYPES.BIOENTITY_LINK.type
    ) {
      return false;
    }
    // Otherwise, allow batch-based lookups even though we're not sure if the sample is a batch.
    return true;
  }

  _canLookupBatchId(): boolean {
    // Top-level Sample lookup
    if (this.state.fieldsChain.length === 0) {
      return this.state.lookupType === LookupType.SAMPLE
        ? this._canTableRowSampleBeBatch()
        : this.state.topLevelSchemaType === ModelTypeConstants.BATCH_SCHEMA;
    }

    const selectedLinkSchemas = this._getSelectedLinkSchemasFromFieldsChain(this.state.fieldsChain);
    return selectedLinkSchemas != null && selectedLinkSchemas.batchSchema != null;
  }

  _getTopLevelSchemaTypeForSampleLookupType(): SchemaType {
    return this._canTableRowSampleBeBatch() ? ModelTypeConstants.BATCH_SCHEMA : ModelTypeConstants.TAG_SCHEMA;
  }

  _getTopLevelSampleSchemaType(): SchemaType {
    // If we're starting from an entity, we can't look up batches
    switch (this.state.lookupType) {
      case LookupType.SAMPLE:
        return this._getTopLevelSchemaTypeForSampleLookupType();
      case LookupType.STAGE_RUN_INPUT:
        return this.state.topLevelSchemaType;
      default:
        throw new Error(`Unexpected lookup type ${this.state.lookupType}`);
    }
  }

  _handleDataTableSelectionChange = (
    dataTableConfig: FromServerDataTableConfig | null,
    dataTableColumn: FromServerDataTableColumn | null
  ): void => {
    const dataTableConfigChanged = this.state.dataTableConfig !== dataTableConfig;
    this.setState({
      dataTableConfig,
      dataTableColumn,
      filterSpecifiers: dataTableConfigChanged ? [] : this.state.filterSpecifiers,
    });
  };

  _handleSchemaFieldSelectionChange = ({
    field,
    schemaType,
  }: {
    field: SchemaField;
    schemaType: SchemaType;
  }): void => {
    this.setState({
      selectedSchemaField: field,
      selectedSchemaFieldSchemaType: schemaType,
    });
  };

  /**
   * If props.rowData is specified (the modal is triggered from a data table or transfer table in an entry)
   * return the tag schema of the sample represented by the rowData.
   */
  _getTableRowSampleTagSchema(
    props: ConfigureLookupFieldModalProps
  ): TagSchemaSummaryWithFieldsGraphQL | null {
    if (props.batch) {
      return props.tagSchemasById[props.batch.file.tagSchema.id];
    }
    if (props.file) {
      return props.file.tagSchema != null ? props.tagSchemasById[props.file.tagSchema.id] : null;
    }
    return null;
  }

  _renderValuesTable(): JSX.Element | null {
    if (!this._hasRowData()) {
      return null;
    }

    const lookupSpecifier = this._getLookupSpecifier();
    if (lookupSpecifier == null) {
      return null;
    }
    const isDataTableSelected =
      this.state.topLevelSchema != null && this.state.dataType === DATA_TYPE.DATA_TABLE;

    const {evaluatedRow, rowData, columnDefinitions} = this.props;
    return (
      <div
        className={classNames({
          'lookupField-dataTableConfig': isDataTableSelected,
          'u-spacing-top': !isDataTableSelected,
        })}
      >
        <LookupFieldValuesTable
          isDataTable={isDataTableSelected}
          batchId={this.props.batchId}
          fileId={this.props.fileId}
          stageRunId={getStageRunId({evaluatedRow, rowData: rowData!, columnDefinitions})}
          lookupSpecifier={lookupSpecifier}
          serverLookupResult={this.state.serverLookupResult}
          onFieldValuesChange={this._handleFieldValuesChange}
        />
      </div>
    );
  }

  _renderDataTableSelectors(): JSX.Element {
    return (
      <div className="lookupField-dataTableConfig">
        <DataTableConfigFieldSelector
          onDataTableColumnChange={this._handleDataTableSelectionChange}
          dataTableConfig={this.state.dataTableConfig}
          dataTableColumn={this.state.dataTableColumn}
        />
        {this.state.dataTableConfig && (
          <FiltersSelector
            dataTableConfig={this.state.dataTableConfig}
            filterSpecifiers={this.state.filterSpecifiers}
            onAddFilter={this._handleAddFilter}
            onDeleteFilter={this._handleDeleteFilter}
            onEditFilter={this._handleEditFilter}
          />
        )}
      </div>
    );
  }

  _getSelectedLinkSchemas(): {tagSchema: TagSchemaSummaryWithFieldsGraphQL; batchSchema: null} {
    let tagSchema = this.state.topLevelSchema!;

    if (this.state.fieldsChain.length > 0) {
      const selectedLinkSchemas = this._getSelectedLinkSchemasFromFieldsChain(this.state.fieldsChain);
      if (selectedLinkSchemas != null && selectedLinkSchemas.tagSchema != null) {
        tagSchema = selectedLinkSchemas.tagSchema;
      }
    }

    return {tagSchema, batchSchema: null};
  }

  _renderSchemaFieldSelectors(): JSX.Element | null {
    const {tagSchema, batchSchema} = this._getSelectedLinkSchemas();
    return (
      <FormGroup>
        <Label>FIELD</Label>
        <LookupFieldSchemaFieldSelector
          tagSchema={tagSchema}
          batchSchema={batchSchema}
          selectedSchemaField={this.state.selectedSchemaField}
          onChange={this._handleSchemaFieldSelectionChange}
        />
      </FormGroup>
    );
  }

  _renderSampleSchemaTypeToInsertSelector(): JSX.Element {
    return (
      <SelectDropdown
        displayName={
          this.state.sampleSchemaTypeToInsert
            ? SCHEMA_TYPE_TO_DISPLAY[this.state.sampleSchemaTypeToInsert]
            : 'Select a field'
        }
        menu={
          <SearchableMenu
            isMulti={false}
            onSelect={(selectedValue) => {
              this.setState({sampleSchemaTypeToInsert: selectedValue as SchemaType});
            }}
            optionDatas={[
              {
                name: SCHEMA_TYPE_TO_DISPLAY[ModelTypeConstants.TAG_SCHEMA],
                value: ModelTypeConstants.TAG_SCHEMA,
              },
              ...(this._canLookupBatchId()
                ? [
                    {
                      name: SCHEMA_TYPE_TO_DISPLAY[ModelTypeConstants.BATCH_SCHEMA],
                      value: ModelTypeConstants.BATCH_SCHEMA,
                    },
                  ]
                : []),
            ]}
            selectedValue={this.state.sampleSchemaTypeToInsert}
            width={MenuWidth.FULL}
          />
        }
      />
    );
  }

  _handleChangeDataType = (dataType: DATA_TYPE): void => {
    this.setState({
      dataType,
      sampleSchemaTypeToInsert: this._canLookupBatchId()
        ? ModelTypeConstants.BATCH_SCHEMA
        : ModelTypeConstants.TAG_SCHEMA,
      dataTableConfig: null,
      dataTableColumn: null,
      ...this._getClearResultsState(),
    });
  };

  _renderFieldSelectors(): JSX.Element | null {
    if (this.state.topLevelSchema == null) {
      return null;
    }
    const {lookupType, fieldsChain, dataType} = this.state;
    return (
      <div className="_test-configureLookupFieldModal-fieldSelectors">
        <LookupFieldDataTypeSelector
          dataType={dataType}
          isStageRunInput={lookupType === LookupType.STAGE_RUN_INPUT}
          fieldsChain={fieldsChain}
          onDataTypeChanged={this._handleChangeDataType}
        />
        {dataType === DATA_TYPE.DATA_TABLE && this._renderDataTableSelectors()}
        {dataType === DATA_TYPE.SCHEMA_FIELD && this._renderSchemaFieldSelectors()}
        {dataType === DATA_TYPE.SAMPLE_ID && this._renderSampleSchemaTypeToInsertSelector()}
        {dataType === DATA_TYPE.INPUT_SAMPLE && this._renderSampleSchemaTypeToInsertSelector()}
      </div>
    );
  }

  _renderNestedLookupFieldSelector(): JSX.Element | null {
    if (this.state.topLevelSchema == null) {
      return null;
    }

    const topLevelSchemaType = this._getTopLevelSampleSchemaType();
    assert(
      topLevelSchemaType != null,
      'topLevelSchemaType is expected to exist when rendering NestedSchemaFieldSelector'
    );
    return (
      <div>
        <NestedSchemaFieldSelector
          selectedFieldsChain={this.state.fieldsChain}
          topLevelSchema={this.state.topLevelSchema}
          onSelectedFieldChainChange={this._handleSelectedFieldsChainChange}
          tagSchemasById={this.props.tagSchemasById}
        />
        {this._renderFieldSelectors()}
      </div>
    );
  }

  _renderForStageRunInputLookupType(): JSX.Element {
    return (
      <div>
        <LookupFieldSchemaSelector
          isSchemaTypeReadonly={false}
          tagSchema={this.state.topLevelSchema}
          schemaType={this.state.topLevelSchemaType}
          tagSchemasById={this.props.tagSchemasById}
          onTagSchemaChanged={this._handleTopLevelTagSchemaChanged}
        />
        {this.state.topLevelSchema == null ? (
          <FormGroup>
            <Label>Insert</Label>
            {this._renderSampleSchemaTypeToInsertSelector()}
          </FormGroup>
        ) : (
          this._renderNestedLookupFieldSelector()
        )}
      </div>
    );
  }

  _renderForExpCondLookupType(): JSX.Element {
    return (
      <div>
        <SelectDropdown
          displayName={
            this.state.experimentalConditionName != null
              ? this.state.experimentalConditionName
              : 'Select experimental condition'
          }
          menu={
            <SearchableMenu
              isMulti={false}
              optionDatas={this.props.experimentalConditionNames.map((name) => ({
                name,
                value: name,
              }))}
              onSelect={(selectedValue) => {
                this.setState({experimentalConditionName: selectedValue});
              }}
              selectedValue={this.state.experimentalConditionName}
            />
          }
        />
      </div>
    );
  }

  _renderForSampleLookupType(): JSX.Element {
    const tableRowSampleTagSchema = this._getTableRowSampleTagSchema(this.props);
    if (this._hasRowData() && tableRowSampleTagSchema == null) {
      // tag schema is expected, but is not loaded yet.
      return <CenteredHorizontalThrobber />;
    }

    return (
      <div>
        {tableRowSampleTagSchema != null ? (
          <div className="u-small-spacing-bottom">
            <Label>Sample schema</Label>
            <div>{tableRowSampleTagSchema.name}</div>
          </div>
        ) : (
          <LookupFieldSchemaSelector
            isSchemaTypeReadonly={true}
            tagSchema={this.state.topLevelSchema}
            schemaType={this.state.topLevelSchemaType}
            tagSchemasById={this.props.tagSchemasById}
            onTagSchemaChanged={this._handleTopLevelTagSchemaChanged}
          />
        )}
        {this._renderNestedLookupFieldSelector()}
      </div>
    );
  }

  override render(): JSX.Element {
    return (
      <Modal title="Set lookup field" onRequestClose={this.props.onRequestClose} buttons={this._getButtons()}>
        <div className="lookupField-configureLookupField">
          {this.props.supportedLookupTypes.length > 1 && (
            <LookupTypeSelector
              supportedLookupTypes={this.props.supportedLookupTypes}
              lookupType={this.state.lookupType}
              onChange={this._handleChangeLookupType}
            />
          )}
          {(() => {
            switch (this.state.lookupType) {
              case LookupType.SAMPLE:
                return this._renderForSampleLookupType();
              case LookupType.STAGE_RUN_INPUT:
                return this._renderForStageRunInputLookupType();
              case LookupType.EXPERIMENTAL_CONDITION:
                return this._renderForExpCondLookupType();
              default:
                throw new Error(`Unexpected lookupType ${this.state.lookupType}`);
            }
          })()}
          {this._renderValuesTable()}
        </div>
      </Modal>
    );
  }
}

function LoadingConfigureLookupFieldModal(props: ConfigureLookupFieldModalProps): JSX.Element {
  return (
    <Modal title="Set lookup field" onRequestClose={props.onRequestClose}>
      <CenteredHorizontalThrobber />
    </Modal>
  );
}

function QueryingConfigureLookupFieldModal(props: ConfigureLookupFieldModalProps): JSX.Element {
  const tagSchemasGql = useTagSchemaSummariesWithFields();
  const tagSchemasById = _.indexBy(tagSchemasGql!, 'id');

  const Component = tagSchemasGql ? ConfigureLookupFieldModal : LoadingConfigureLookupFieldModal;

  return <Component {...props} tagSchemasById={tagSchemasById} />;
}

export default connect(mapStateToProps)(
  StorableStore.connect(mapStorableStoreToProps)(QueryingConfigureLookupFieldModal)
);
